import { HTMLAttributes, useCallback } from 'react';
import styled from 'styled-components';
import { Flex, Chip, Column, Switch } from '@elementary/common-ui/components';
import Menu from '@elementary/common-ui/components/Menu';
import { Tooltip } from '@mui/material';
import { FieldChange, Period } from '@elementary/common-ui/types';
import { pluralizeString } from '@elementary/common-ui/utils/common';
import { COUNT_OPTIONS, PERIOD_OPTIONS, PERIOD_PLURAL } from './constants';
import {
  CellFlex,
  ClickableChip,
  ElasticChipWrapper,
  FixedChipWrapper,
} from './styles';
import { BasePreviewRow } from './types';
import { formatThreshold } from './utils';

type NewValueChipProps = {
  label: string;
  title: string;
  disableHoverListener: boolean;
} & HTMLAttributes<HTMLSpanElement>;

const NewValueChip = ({
  label,
  title,
  disableHoverListener,
  ...spanProps
}: NewValueChipProps) => (
  <Tooltip title={title} disableHoverListener={disableHoverListener}>
    <span {...spanProps}>
      <ClickableChip label={label} customVariant="dashed" />
    </span>
  </Tooltip>
);

type ListColumnOptions = {
  filterNewToOptions?: boolean;
  isElasticLayout?: boolean;
  wrapCell?: boolean;
};

const ListCellFlex = styled(CellFlex)<{ $wrap?: boolean }>`
  ${({ $wrap }) => $wrap && 'flex-wrap: wrap;'}
`;

const useConfigPreviewColumnBuilders = <TRow extends BasePreviewRow<unknown>>(
  hasResponded: boolean,
) => {
  const buildPeriodColumn = useCallback(
    (
      id: string,
      header: string,
      getField: (row: TRow) => FieldChange<Period | null> | undefined,
      onChange: (row: TRow, newValue: Period | null) => void,
      nullable?: boolean,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel = formatThreshold(field.current);
        const newLabel = formatThreshold(field.new);
        const isChanged = currentLabel !== newLabel;
        const showCurrent = isChanged && !row.isCreateRow;

        if (hasResponded) {
          return (
            <Flex sx={{ gap: 1, alignItems: 'center' }}>
              {showCurrent && (
                <Chip label={currentLabel} customVariant="crossed" />
              )}
              <ClickableChip label={newLabel} customVariant="dashed" />
            </Flex>
          );
        }

        if (!field.new) {
          return (
            <Flex sx={{ gap: 1, alignItems: 'center' }}>
              {showCurrent && (
                <Chip label={currentLabel} customVariant="crossed" />
              )}
              <ClickableChip
                label={newLabel}
                customVariant="dashed"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(row, { period: 'day', count: 1 });
                }}
              />
            </Flex>
          );
        }

        return (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            {showCurrent && (
              <Chip label={currentLabel} customVariant="crossed" />
            )}
            <Flex sx={{ gap: 0.5, alignItems: 'center' }}>
              <Menu
                menuButton={
                  <ClickableChip
                    label={String(field.new.count)}
                    customVariant="dashed"
                  />
                }
                options={COUNT_OPTIONS}
                selectedOptions={[String(field.new.count)]}
                onChange={(value) =>
                  onChange(row, {
                    ...field.new!,
                    count: Number(value),
                  })
                }
                closeOnChange
              />
              <Menu
                menuButton={
                  <ClickableChip
                    label={
                      PERIOD_PLURAL[field.new.period][
                        field.new.count === 1 ? 0 : 1
                      ]
                    }
                    customVariant="dashed"
                    onDelete={
                      nullable
                        ? (e) => {
                            e.stopPropagation();
                            onChange(row, null);
                          }
                        : undefined
                    }
                  />
                }
                options={PERIOD_OPTIONS}
                selectedOptions={[field.new.period]}
                onChange={(value) =>
                  onChange(row, {
                    ...field.new!,
                    period: value as Period['period'],
                  })
                }
                closeOnChange
              />
            </Flex>
          </Flex>
        );
      },
    }),
    [hasResponded],
  );

  const buildDropdownColumn = useCallback(
    (
      id: string,
      header: string,
      options: { value: string; label: string }[],
      getField: (row: TRow) => FieldChange<string | null> | undefined,
      onChange: (row: TRow, newValue: string | null) => void,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel =
          options.find((o) => o.value === field.current)?.label ??
          field.current ??
          'None';
        const newLabel =
          options.find((o) => o.value === field.new)?.label ??
          field.new ??
          'None';
        const isChanged = field.current !== field.new;
        const showCurrent = isChanged && !row.isCreateRow;

        if (hasResponded) {
          return (
            <Flex sx={{ gap: 1, alignItems: 'center' }}>
              {showCurrent && (
                <Chip label={currentLabel} customVariant="crossed" />
              )}
              <ClickableChip label={newLabel} customVariant="dashed" />
            </Flex>
          );
        }

        return (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            {showCurrent && (
              <Chip label={currentLabel} customVariant="crossed" />
            )}
            <Menu
              menuButton={
                <ClickableChip label={newLabel} customVariant="dashed" />
              }
              options={options}
              selectedOptions={field.new ? [field.new] : []}
              onChange={(value) => onChange(row, value)}
              closeOnChange
            />
          </Flex>
        );
      },
    }),
    [hasResponded],
  );

  const buildBooleanColumn = useCallback(
    (
      id: string,
      header: string,
      trueLabel: string,
      falseLabel: string,
      getField: (row: TRow) => FieldChange<boolean> | undefined,
      onChange: (row: TRow, newValue: boolean) => void,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;
        const currentLabel = field.current ? trueLabel : falseLabel;
        const isChanged = field.current !== field.new;

        return (
          <Flex sx={{ gap: 1, alignItems: 'center' }}>
            <Chip
              label={currentLabel}
              customVariant={isChanged ? 'crossed' : undefined}
            />
            <Switch
              checked={field.new}
              disabled={hasResponded}
              onClick={(e) => e.stopPropagation()}
              onChange={() => onChange(row, !field.new)}
            />
          </Flex>
        );
      },
    }),
    [hasResponded],
  );

  const buildListColumn = useCallback(
    (
      id: string,
      header: string,
      itemLabel: [string, string],
      getField: (row: TRow) => FieldChange<string[]> | undefined,
      onChange: (row: TRow, newValue: string[]) => void,
      allOptions: { value: string; label: string }[],
      {
        filterNewToOptions = false,
        isElasticLayout = false,
        wrapCell = false,
      }: ListColumnOptions = {},
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      width: 200,
      renderer: (row: TRow) => {
        const field = getField(row);
        if (!field) return <></>;

        const validValues = filterNewToOptions
          ? new Set(allOptions.map((o) => o.value))
          : null;
        const validNew = validValues
          ? field.new.filter((v) => validValues.has(v))
          : field.new;

        const formatList = (items: string[]) =>
          items.length <= 1
            ? items[0] || 'None'
            : pluralizeString(items.length, ...itemLabel);

        const currentDisplay = formatList(field.current);
        const newDisplay = formatList(validNew);
        const currentFull = field.current.join(', ');
        const newFull = validNew.join(', ');
        const isChanged =
          field.current.slice().sort().join(',') !==
          validNew.slice().sort().join(',');

        const showCurrent = isChanged && !row.isCreateRow;

        const currentChip = isElasticLayout ? (
          <FixedChipWrapper>
            <Tooltip title={currentFull} disableHoverListener={!isChanged}>
              <span>
                <Chip label={currentDisplay} customVariant="crossed" />
              </span>
            </Tooltip>
          </FixedChipWrapper>
        ) : (
          <Chip label={currentDisplay} customVariant="crossed" />
        );

        const newChip = (
          <NewValueChip
            label={newDisplay}
            title={newFull}
            disableHoverListener={validNew.length <= 1}
          />
        );

        if (hasResponded) {
          return (
            <ListCellFlex $wrap={wrapCell}>
              {showCurrent && currentChip}
              {isElasticLayout ? (
                <ElasticChipWrapper>{newChip}</ElasticChipWrapper>
              ) : (
                newChip
              )}
            </ListCellFlex>
          );
        }

        return (
          <ListCellFlex $wrap={wrapCell}>
            {showCurrent && currentChip}
            {isElasticLayout ? (
              <ElasticChipWrapper>
                <Menu
                  menuButton={newChip}
                  options={allOptions}
                  selectedOptions={validNew}
                  onChange={(values) => onChange(row, values)}
                  multiple
                  showSearch
                  showClear
                />
              </ElasticChipWrapper>
            ) : (
              <Menu
                menuButton={newChip}
                options={allOptions}
                selectedOptions={field.new}
                onChange={(values) => onChange(row, values)}
                multiple
                showSearch
                showClear
              />
            )}
          </ListCellFlex>
        );
      },
    }),
    [hasResponded],
  );

  const buildParamsColumn = useCallback(
    (
      id: string,
      header: string,
      getParams: (
        row: TRow,
      ) => Record<string, FieldChange<unknown>> | undefined,
    ): Column<TRow> => ({
      id,
      header,
      showValueWhenEmpty: true,
      filterable: false,
      nonToggleable: true,
      width: 240,
      renderer: (row: TRow) => {
        const params = getParams(row);
        if (!params || Object.keys(params).length === 0) return <></>;
        return (
          <ListCellFlex $wrap>
            {Object.entries(params).map(([key, change]) => (
              <Chip
                key={key}
                label={`${key}: ${String(change?.new ?? '')}`}
                customVariant="dashed"
              />
            ))}
          </ListCellFlex>
        );
      },
    }),
    [],
  );

  return {
    buildPeriodColumn,
    buildDropdownColumn,
    buildBooleanColumn,
    buildListColumn,
    buildParamsColumn,
  };
};

export default useConfigPreviewColumnBuilders;
